import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Col, Flex, Form, Input, Row, message } from 'antd';
import { useState } from 'react';

import {
  Database_Procs_AdminMoveGetSchoolTeams_Item,
  Entities_Team_UpdateInfo_Request,
} from '@suntex.fim/api-client';
import { $SpecialGradeLevel, SpecialGradeLevel } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import { teamGradeOptions, teamRosterKeys } from '@suntex.fim/team';
import {
  Button,
  ColumnType,
  Modal,
  SearchInput,
  Select,
  Table,
} from '@suntex.fim/ui';

import { useSchool, useSchoolTeams } from '../hooks';

import {
  CLASS_NAME_MAX_LENGTH,
  DISPLAY_NAME_MAX_LENGTH,
  TeamEdits,
  buildTeamUpdateRequest,
  createTeamEdits,
  describeGrade,
  isTeamEditValid,
} from './viewEditTeamsUtils';

/**
 * returns JSX for viewing and editing teams
 */
export function ViewEditTeams() {
  const { api } = useFramework();
  const { schoolId } = useSchool();
  const { teams, isLoading, isError, reload } = useSchoolTeams();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingTeam, setEditingTeam] =
    useState<Database_Procs_AdminMoveGetSchoolTeams_Item | null>(null);
  const [edits, setEdits] = useState<TeamEdits>({
    displayName: '',
    className: '',
    grade: SpecialGradeLevel.Kindergarten,
  });

  const updateTeam = useMutation({
    mutationFn: (request: Entities_Team_UpdateInfo_Request) =>
      api.put_Team_Update(request),
    onSuccess: (_response, request) => {
      message.success('Team updated successfully!');
      setEditingTeam(null);
      reload();
      void queryClient.invalidateQueries({
        queryKey: ['team', 'info', request.teamId],
      });
      void queryClient.invalidateQueries({
        queryKey: teamRosterKeys.summary(request.teamId),
      });
    },
    onError: () => {
      message.error('Unable to update team.');
    },
  });

  const openEditModal = (team: Database_Procs_AdminMoveGetSchoolTeams_Item) => {
    if (updateTeam.isPending) return;
    setEditingTeam(team);
    setEdits(createTeamEdits(team));
  };

  const closeEditModal = () => {
    if (updateTeam.isPending) return;
    setEditingTeam(null);
  };

  const saveEdit = () => {
    if (!editingTeam || updateTeam.isPending || !isTeamEditValid(edits)) return;
    updateTeam.mutate(buildTeamUpdateRequest(editingTeam, schoolId!, edits));
  };

  const columns: ColumnType<Database_Procs_AdminMoveGetSchoolTeams_Item>[] = [
    { id: 'teamName', accessorKey: 'teamName', header: 'Team Name' },
    { id: 'className', accessorKey: 'className', header: 'Class Name' },
    {
      id: 'grade',
      accessorKey: 'grade',
      header: 'Grade',
      cell: (info) => describeGrade(info.getValue<number>()),
    },
    { id: 'teamContact', accessorKey: 'teamContact', header: 'Teacher' },
    { id: 'students', accessorKey: 'students', header: 'Students' },
    {
      id: 'actions',
      header: 'Actions',
      enableGlobalFilter: false,
      cell: ({ row }) => (
        <Button
          intent="secondary"
          disabled={updateTeam.isPending}
          onClick={() => openEditModal(row.original)}
        >
          Edit
        </Button>
      ),
    },
  ];

  if (schoolId === null) return <p>School information is unavailable.</p>;
  if (isError) return <p>Unable to load teams.</p>;

  return (
    <Flex vertical gap="20px" style={{ minWidth: 0 }}>
      <Row gutter={10} className="main-navigation">
        <Col>
          <Flex gap="10px" className="main-navigation__heading" align="center">
            <h3>View and Edit Teams</h3>
          </Flex>
        </Col>
      </Row>
      <SearchInput value={searchQuery} onChange={setSearchQuery} />
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table
          data={teams}
          isLoading={isLoading}
          globalFilter={searchQuery}
          columns={columns}
        />
      </div>
      <Modal
        closable={false}
        centered
        title="Edit Team"
        open={editingTeam !== null}
        onCancel={closeEditModal}
        width="626px"
        footer={[
          <Button
            key="cancel"
            intent="secondary"
            disabled={updateTeam.isPending}
            onClick={closeEditModal}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            intent="primary"
            disabled={!isTeamEditValid(edits) || updateTeam.isPending}
            onClick={saveEdit}
          >
            {updateTeam.isPending ? 'Saving...' : 'Save Changes'}
          </Button>,
        ]}
      >
        <Form layout="vertical">
          {/* eslint-disable-next-line no-magic-numbers -- gap in px for both axes of the form grid */}
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Team Name">
                <Input disabled value={editingTeam?.teamName} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Teacher Display Name"
                htmlFor="team-edit-display-name"
                required
              >
                <Input
                  id="team-edit-display-name"
                  disabled={updateTeam.isPending}
                  maxLength={DISPLAY_NAME_MAX_LENGTH}
                  value={edits.displayName}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      displayName: e.target.value,
                    }))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Class Name"
                htmlFor="team-edit-class-name"
                required
              >
                <Input
                  id="team-edit-class-name"
                  disabled={updateTeam.isPending}
                  maxLength={CLASS_NAME_MAX_LENGTH}
                  value={edits.className}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, className: e.target.value }))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Grade" htmlFor="team-edit-grade">
                <Select.One
                  id="team-edit-grade"
                  disabled={updateTeam.isPending}
                  value={edits.grade}
                  onChange={(grade) =>
                    setEdits((prev) => ({
                      ...prev,
                      grade: $SpecialGradeLevel.from(grade),
                    }))
                  }
                  options={teamGradeOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Flex>
  );
}
